import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IoService } from '../../../services/io/io.service';
import { AppComponent } from 'src/app/app.component';

// One table row: a location with an alternate price (from priceRows with a
// price override) or one the user just added. Prices are edited in dollars and
// converted to integer cents on save; a removed or cleared row deletes that
// location's override so the standard price applies again.
interface LocationPriceRow {
  locationId: string;
  locationName: string;
  price: number | null;
  originalPrice: number | null;
  isNew: boolean;
  removed: boolean;
}

@Component({
  selector: 'app-inventory-location-pricing',
  templateUrl: './inventory-location-pricing.component.html',
  styleUrls: ['./inventory-location-pricing.component.scss']
})
export class InventoryLocationPricingComponent implements OnInit {

  accountName: string = null;
  standardPrice: number = null;
  rows: LocationPriceRow[] = [];
  // Locations the account sells at that have no override yet (add candidates).
  availableLocations: { locationId: string, locationName: string }[] = [];
  addLocationId = '';
  loading = false;
  saving = false;
  loadError: string = null;
  errors: string[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<InventoryLocationPricingComponent>,
    private ioService: IoService,
  ) { }

  ngOnInit() {
    this.load();
  }

  async load() {
    this.loading = true;
    this.loadError = null;
    try {
      const pricing: any = await this.ioService.post('/account/getInventoryPricing', {
        accountId: this.data.documentId,
      });
      this.applyPricing(pricing);
    } catch (e) {
      this.loadError = e?.error?.msg || 'Failed to load pricing.';
    }
    this.loading = false;
  }

  private applyPricing(pricing: any) {
    this.accountName = pricing.name;
    this.standardPrice = pricing.price != null ? pricing.price : null;
    this.rows = (pricing.priceRows || [])
      .filter(row => row.price !== null && row.price !== undefined)
      .map(row => ({
        locationId: String(row.locationId),
        locationName: row.locationName,
        price: row.price / 100,
        originalPrice: row.price,
        isNew: false,
        removed: false,
      }));
    this.availableLocations = (pricing.priceRows || [])
      .filter(row => row.price === null || row.price === undefined)
      .map(row => ({
        locationId: String(row.locationId),
        locationName: row.locationName,
      }));
    this.addLocationId = '';
  }

  addLocation(locationId: string) {
    this.addLocationId = locationId;
    const location = this.availableLocations.find(l => l.locationId === locationId);
    if (!location) return;
    this.rows.push({
      locationId: location.locationId,
      locationName: location.locationName,
      price: null,
      originalPrice: null,
      isNew: true,
      removed: false,
    });
    this.availableLocations = this.availableLocations.filter(l => l.locationId !== location.locationId);
    // Reset on the next tick — resetting synchronously races the select's own
    // value write, leaving the first remaining option displayed instead of the
    // "Add a location" placeholder.
    setTimeout(() => this.addLocationId = '');
  }

  removeRow(row: LocationPriceRow) {
    if (row.isNew) {
      this.rows = this.rows.filter(r => r !== row);
      this.availableLocations = [
        ...this.availableLocations,
        { locationId: row.locationId, locationName: row.locationName },
      ].sort((a, b) => a.locationName.localeCompare(b.locationName));
    } else {
      row.removed = !row.removed;
    }
  }

  // ----- Save -----

  private toCents(dollars: number): number {
    return Math.round(dollars * 100);
  }

  rowInvalid(row: LocationPriceRow): boolean {
    return row.isNew && !row.removed && (row.price === null || row.price === undefined || String(row.price) === '');
  }

  hasChanges(): boolean {
    return this.rows.some(row =>
      row.isNew || row.removed ||
      (row.price !== null && String(row.price) !== '' ? this.toCents(row.price) : null) !== row.originalPrice
    );
  }

  async save() {
    this.errors = [];

    const invalidRows = this.rows.filter(row => this.rowInvalid(row));
    if (invalidRows.length > 0) {
      this.errors = ['Price is required for ' + invalidRows.map(row => row.locationName).join(', ') + '.'];
      return;
    }

    const priceLocation: any = {};
    for (const row of this.rows) {
      if (row.removed) {
        // Empty string deletes the override server side.
        priceLocation[row.locationId] = '';
        continue;
      }
      const newValue = (row.price !== null && row.price !== undefined && String(row.price) !== '')
        ? this.toCents(row.price) : null;
      if (newValue !== row.originalPrice) {
        priceLocation[row.locationId] = newValue !== null ? newValue : '';
      }
    }

    this.saving = true;
    try {
      const pricing: any = await this.ioService.post('/account/updateInventoryPricing', {
        accountId: this.data.documentId,
        priceLocation,
      });
      this.applyPricing(pricing);
      AppComponent.showSnack('Location prices saved.', 'OK', 3000);
    } catch (e) {
      this.errors = [e?.error?.msg || 'Failed to save location prices.'];
    }
    this.saving = false;
  }

  close() {
    this.dialogRef.close();
  }
}
